import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { filieresLicence, filieresMaster } from '@/data/filieres';
import { FaGraduationCap, FaClock, FaArrowRight, FaSearch } from 'react-icons/fa';

export default function Filieres() {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const getFilteredFilieres = () => {
    let filieres = [];
    if (activeTab === 'all') {
      filieres = [...filieresLicence, ...filieresMaster];
    } else if (activeTab === 'licence') {
      filieres = filieresLicence;
    } else {
      filieres = filieresMaster;
    }

    if (searchQuery) {
      filieres = filieres.filter(f => 
        f.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.debouches?.some(d => d.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    return filieres;
  };

  const filteredFilieres = getFilteredFilieres();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-brand-secondary to-brand-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Nos Filières</h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto">
            Découvrez nos {filieresLicence.length} programmes de Licence et {filieresMaster.length} programmes de Master 
            conçus pour votre réussite professionnelle
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          {/* Tabs */}
          <div className="flex gap-2 bg-white p-1 rounded-lg shadow-sm">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-3 rounded-md font-semibold transition-all ${
                activeTab === 'all'
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Toutes ({filieresLicence.length + filieresMaster.length})
            </button>
            <button
              onClick={() => setActiveTab('licence')}
              className={`px-6 py-3 rounded-md font-semibold transition-all ${
                activeTab === 'licence'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FaGraduationCap className="inline mr-2" />
              Licence ({filieresLicence.length})
            </button>
            <button
              onClick={() => setActiveTab('master')}
              className={`px-6 py-3 rounded-md font-semibold transition-all ${
                activeTab === 'master'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FaGraduationCap className="inline mr-2" />
              Master ({filieresMaster.length})
            </button>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-auto">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une filière..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-3 border border-gray-200 rounded-lg w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>
        </div>

        {/* Results count */}
        <p className="text-gray-500 mb-6">
          {filteredFilieres.length} filière{filteredFilieres.length > 1 ? 's' : ''} trouvée{filteredFilieres.length > 1 ? 's' : ''}
        </p>

        {/* Filières Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFilieres.map((filiere) => (
            <Card 
              key={filiere.slug} 
              className="overflow-hidden hover:shadow-xl transition-all duration-300 group"
              data-testid={`filiere-card-${filiere.slug}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={filiere.image} 
                  alt={filiere.nom}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    filiere.type === 'licence' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-purple-500 text-white'
                  }`}>
                    {filiere.type === 'licence' ? 'LICENCE' : 'MASTER'}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-bold text-white line-clamp-2">{filiere.nom}</h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <FaClock className="text-brand-primary" />
                    {filiere.duree}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono font-bold">
                    {filiere.code}
                  </span>
                </div>

                {/* Débouchés (top 3) */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Débouchés :</p>
                  <div className="flex flex-wrap gap-1">
                    {filiere.debouches?.slice(0, 3).map((deb, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-2 py-1 bg-brand-primary/10 text-brand-primary rounded"
                      >
                        {deb}
                      </span>
                    ))}
                    {filiere.debouches?.length > 3 && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded">
                        +{filiere.debouches.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                <Link to={`/filieres/${filiere.slug}`}>
                  <Button 
                    className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white group"
                    data-testid={`btn-voir-${filiere.slug}`}
                  >
                    Voir les détails
                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>

        {/* No results */}
        {filteredFilieres.length === 0 && (
          <div className="text-center py-16">
            <FaSearch className="text-5xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune filière trouvée pour "{searchQuery}"</p>
            <Button 
              onClick={() => setSearchQuery('')}
              variant="outline"
              className="mt-4"
            >
              Réinitialiser la recherche
            </Button>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-brand-primary to-brand-secondary text-white p-8 sm:p-12 rounded-2xl shadow-xl text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Prêt à commencer votre parcours ?</h2>
          <p className="text-lg text-white/80 mb-6 max-w-2xl mx-auto">
            Inscrivez-vous dès maintenant et rejoignez notre communauté d'excellence à l'Université CEFAT International
          </p>
          <a href="https://scolarite-cefat.net/preinscription">
            <Button 
              data-testid="filieres-cta-btn"
              className="bg-white text-brand-primary hover:bg-brand-accent font-bold px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
            >
              Je me préinscris maintenant
              <FaArrowRight className="ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
